// Shrink roles: decide what becomes of each role (keep, merge into another, not needed), see the
// roles you'd end up with and what each can do, and the checks - a role that could raise and approve
// the same thing, approvals only the Owner could give, duties nobody but the Owner would hold - then
// apply it. Merged roles' people move across; unused roles are switched off, not deleted.
import { useMemo, useState } from "react";
import { Card, Btn, Table, Tr, Td, Loading, Pill } from "@/components/kit";
import api from "@/lib/api";
import { act, input, k0, useLoad } from "@/components/erp";

// the starting suggestion for a small team (six roles)
const SUGGESTED = {
  fate: { general_manager: "off", finance_manager: "off", accountant: "keep", procurement: "accountant", hr_admin: "accountant", cashier: "keep",
    auditor: "off", ops_manager: "keep", workshop_manager: "ops_manager", transport_officer: "keep", mechanic: "transport_officer",
    storekeeper: "transport_officer", depot_attendant: "keep" },
  labels: { accountant: "Accounts & Admin", ops_manager: "Operations Manager", transport_officer: "Yard / Dispatch Clerk", cashier: "Cashier (M-Pesa)" },
};
// raising something and approving the same kind of thing
const PAIRS = [["lpo.create", "lpo.approve."], ["payment.initiate", "payment.approve."], ["float.request", "float.approve"],
  ["fuel.outside.request", "fuel.outside.approve"], ["jobcard.open", "jobcard.approve"], ["quote.request", "jobcard.approve"],
  ["stores.receive", "lpo.approve."], ["payees.manage", "payment.approve."], ["expense.request", "float.approve"]];

export default function RolePlanner() {
  const { data: roles, reload } = useLoad("/auth/roles");
  const { data: perms } = useLoad("/auth/permissions");
  const { data: rules } = useLoad("/approvals/rules");
  const [fate, setFate] = useState({});
  const [labels, setLabels] = useState({});
  const [who, setWho] = useState({});
  const plabel = useMemo(() => Object.fromEntries((perms || []).map((p) => [p.key, p.label])), [perms]);

  const result = useMemo(() => {
    if (!roles) return null;
    const byKey = Object.fromEntries(roles.map((r) => [r.key, r]));
    const f = (k) => fate[k] || (byKey[k]?.active === false ? "off" : "keep");
    const kept = roles.filter((r) => r.key !== "admin" && f(r.key) === "keep").map((r) => {
      const merged = roles.filter((m) => f(m.key) === r.key);
      const all = new Set([...r.perms, ...merged.flatMap((m) => m.perms)]);
      return { ...r, label: labels[r.key] || r.label, merged, perms: [...all] };
    });
    const conflicts = [];
    kept.forEach((r) => PAIRS.forEach(([a, b]) => {
      if (r.perms.includes(a) && r.perms.some((p) => p.startsWith(b))) {
        conflicts.push(`${r.label} can both ${(plabel[a] || a).toLowerCase()} and ${(plabel[r.perms.find((p) => p.startsWith(b))] || b).toLowerCase()} - the system still stops anyone approving their own, so someone else approves those`);
      }
    }));
    const onlyOwner = [];
    const stuck = [];
    (rules || []).forEach((rule) => {
      const mine = rule.steps.filter((st) => !kept.some((r) => r.perms.includes(st.perm)));
      rule.steps.forEach((st, i) => {
        if (!kept.some((r) => r.perms.includes(st.perm))) onlyOwner.push(`${rule.label} - level ${i + 1}${st.min_amount ? ` (from KES ${k0(st.min_amount)})` : ""}`);
      });
      // nobody approves two levels of one request - two levels only the Owner holds can never both be approved
      if (mine.length > 1) stuck.push(`${rule.label}: from KES ${k0(Math.max(...mine.map((s) => s.min_amount)))} it needs ${mine.length} levels only you hold - it would get stuck. Give one level to another role, or make it one level.`);
    });
    const held = new Set(kept.flatMap((r) => r.perms));
    const lost = (perms || []).filter((p) => !held.has(p.key) && !/approve|final/.test(p.key)).map((p) => p.label);
    return { kept, f, conflicts, onlyOwner, lost, stuck };
  }, [roles, rules, perms, fate, labels, plabel]);

  if (!roles || !perms || !rules || !result) return <Loading />;
  const others = roles.filter((r) => r.key !== "admin");
  const targets = (key) => others.filter((r) => r.key !== key && result.f(r.key) === "keep");
  const apply = async () => {
    const plan = Object.fromEntries(others.map((r) => [r.key, result.f(r.key)]));
    if (!window.confirm(`Apply: ${result.kept.length + 1} roles (with the Owner). Merged roles' people move across; unused roles are switched off (they can be switched back on). Go ahead?`)) return;
    if (await act(() => api.post("/auth/roles/plan", { fate: plan, labels }), "Roles updated - affected people sign in again")) { setFate({}); setLabels({}); reload(); }
  };
  return (
    <div className="mt-4 space-y-5">
      <Card className="bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="max-w-3xl text-sm text-slate-600">For each role: keep it, merge it into another (its duties and people move there), or mark it not needed.
            Nothing is deleted - switched-off roles can come back as the company grows.</p>
          <div className="flex gap-2">
            <Btn onClick={() => { setFate(SUGGESTED.fate); setLabels(SUGGESTED.labels); }}>Start from the suggested 6</Btn>
            <Btn onClick={() => { setFate({}); setLabels({}); }}>Reset</Btn>
          </div>
        </div>
        <Table minWidth={900} cols={[{ label: "Side" }, { label: "Role today" }, { label: "What it does" }, { label: "People" }, { label: "Becomes" }, { label: "Name / who" }]}>
          <Tr><Td>Admin</Td><Td className="font-semibold">Owner / Director</Td><Td className="text-xs text-slate-600">Everything, final approvals</Td><Td>{roles.find((r) => r.key === "admin")?.users}</Td><Td><Pill kind="green">Always kept</Pill></Td><Td /></Tr>
          {others.map((r) => {
            const v = result.f(r.key);
            return (
              <Tr key={r.key} className={v === "off" ? "opacity-60" : ""}>
                <Td>{r.side === "admin" ? "Admin" : "Ops"}</Td>
                <Td className="font-medium">{r.label}</Td>
                <Td className="max-w-[300px] text-xs text-slate-600">{r.about}</Td>
                <Td>{r.users}</Td>
                <Td>
                  <select aria-label={`What becomes of ${r.label}`} className={`${input} min-w-[200px]`} value={v} onChange={(e) => setFate({ ...fate, [r.key]: e.target.value })}>
                    <option value="keep">Keep</option>
                    {targets(r.key).map((t) => <option key={t.key} value={t.key}>Merge into {labels[t.key] || t.label}</option>)}
                    <option value="off">Not needed now</option>
                  </select>
                </Td>
                <Td>{v === "keep" ? (
                  <div className="space-y-1">
                    <input className={input} placeholder="Name of the role" value={labels[r.key] ?? r.label} onChange={(e) => setLabels({ ...labels, [r.key]: e.target.value })} />
                    <input className={input} placeholder="Who (for planning)" value={who[r.key] || ""} onChange={(e) => setWho({ ...who, [r.key]: e.target.value })} />
                  </div>) : v === "off" ? <span className="text-xs text-slate-500">switched off</span> : <span className="text-xs text-slate-500">duties and people move</span>}</Td>
              </Tr>
            );
          })}
        </Table>
      </Card>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card title={`You'd have ${result.kept.length + 1} roles`} className="bg-white">
          <ul className="mt-3 space-y-3 text-sm">
            <li><b>Owner / Director</b> - everything</li>
            {result.kept.map((r) => (
              <li key={r.key}><b>{r.label}</b>{who[r.key] ? ` (${who[r.key]})` : ""} - {r.perms.length} things they can do
                {r.merged.length > 0 && <div className="text-xs text-slate-500">takes over: {r.merged.map((m) => m.label).join(", ")}</div>}
                <details className="mt-1 text-xs text-slate-600"><summary className="cursor-pointer text-sky-700">What they can do</summary>
                  <ul className="mt-1 list-disc pl-5">{r.perms.map((p) => <li key={p}>{plabel[p] || p}</li>)}</ul></details>
              </li>
            ))}
          </ul>
        </Card>
        <Card title="Checks" className="bg-white">
          {result.stuck.length > 0 && (<>
            <h3 className="mt-3 text-sm font-semibold text-rose-700">Approvals that would get stuck</h3>
            <ul className="mt-1 list-disc pl-5 text-sm text-rose-700">{result.stuck.map((x) => <li key={x}>{x}</li>)}</ul>
          </>)}
          <h3 className="mt-3 text-sm font-semibold">Only you (the Owner) would approve</h3>
          {result.onlyOwner.length ? <ul className="mt-1 list-disc pl-5 text-sm text-amber-800">{result.onlyOwner.map((x) => <li key={x}>{x}</li>)}</ul>
            : <p className="mt-1 text-sm text-emerald-700">Every approval has someone besides you.</p>}
          <h3 className="mt-4 text-sm font-semibold">Duties only you would hold</h3>
          {result.lost.length ? <ul className="mt-1 list-disc pl-5 text-sm text-amber-800">{result.lost.map((x) => <li key={x}>{x}</li>)}</ul>
            : <p className="mt-1 text-sm text-emerald-700">Every duty has a role.</p>}
          <h3 className="mt-4 text-sm font-semibold">Roles that could raise and approve the same thing</h3>
          {result.conflicts.length ? <ul className="mt-1 list-disc pl-5 text-sm text-amber-800">{result.conflicts.map((x) => <li key={x}>{x}</li>)}</ul>
            : <p className="mt-1 text-sm text-emerald-700">None - raising and approving stay with different roles.</p>}
          <Btn kind="primary" className="mt-5" onClick={apply}>Apply these roles</Btn>
        </Card>
      </div>
    </div>
  );
}
